import React, { useLayoutEffect, useRef, useState } from 'react'
import { Box, IconButton, Stack, Tab, Tabs } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { PANEL_EDGE_BUTTON_WIDTH, PANEL_TAB, RESIZE_AXIS, libraryHeightOf, resizedPreferences } from './processLibraryPanelPreferences'
import ResizeGrip from './ResizeGrip'

/**
 * ProcessLibraryPanel.jsx — story 167 (HU-01, HU-02 · RN-01, RN-02, RN-03, D0, D3, D16).
 * The panel that replaces the graph palette, in its place. It only lays out: the mini tree and the library
 * are handed in already built (`processTree`, `library`), and the preferences live in `WorkflowFlow`.
 *
 * Switching tabs HIDES a zone, it never unmounts it: what is folded, searched or chosen survives (CA1.4).
 * Folding the panel DOES unmount its content, as closing the palette always did (`Nodes.jsx`, `Slide …
 * unmountOnExit`): the version chosen by hand and the "Pending to add" filter start again (#152 S11, S82).
 */
const HANDLE_PX = 6
// PO, 29/09/2026: Tree → Mixed brings the library up from the bottom to its half, Mixed → Library carries it on up
// until it covers everything, and back the same way. Heights in px, because a height of "auto" does not animate.
const ZONE_TRANSITION = 'height 320ms cubic-bezier(.4, 0, .2, 1)'

const TABS = [
  { value: PANEL_TAB.PROCESS, labelKey: 'Tree' },
  { value: PANEL_TAB.MIXED, labelKey: 'Mixed' },
  { value: PANEL_TAB.LIBRARY, labelKey: 'Library' }
]

/**
 * The fold strip of the palette of before this story (PO, 28/09/2026): a thin button the height of what it folds,
 * with only an arrow — glued to the panel's right edge when it is open, alone on the canvas's left edge when not.
 */
const EdgeButton = ({ testId, open, disabled = false, label, onClick, style }) => (
  <Box data-node-toolbar-obstacle style={style} sx={{ position: 'absolute', top: 0, zIndex: 6, display: 'flex', width: PANEL_EDGE_BUTTON_WIDTH }}>
    <IconButton
      data-testid={testId}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      sx={{ flex: 1, minWidth: 0, p: 0, borderRadius: 0, bgcolor: 'background.default', borderRight: 1, borderBottom: 1, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
    >
      {open ? <ChevronLeftIcon fontSize='small' /> : <ChevronRightIcon fontSize='small' />}
    </IconButton>
  </Box>
)

const ProcessLibraryPanel = ({ preferences, onPreferencesChange, editing = false, canvasHeight = 0, processTree, library }) => {
  const { t_ } = useSmartTranslation()
  // RN-01.3 / RN-04.6: without edit mode the panel is folded, and what is remembered is not touched.
  const open = editing && preferences.open
  // Always the whole canvas (PO, 29/09/2026: the bottom handle was unnecessary).
  const panelHeight = canvasHeight > 0 ? canvasHeight : '100%'
  const libraryHeight = libraryHeightOf(preferences, canvasHeight)
  const mixed = preferences.tab === PANEL_TAB.MIXED
  const bodyRef = useRef(null)
  const [bodyHeight, setBodyHeight] = useState(0)
  const [resizing, setResizing] = useState(false)

  useLayoutEffect(() => {
    const body = bodyRef.current
    if (!body) {
      return undefined
    }
    const measure = () => setBodyHeight(body.clientHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(body)
    return () => observer.disconnect()
  }, [open])

  // Where the library stands on each tab: hidden under the bottom edge, on its remembered half, or all the way up.
  const libraryTarget = () => {
    if (preferences.tab === PANEL_TAB.PROCESS) {
      return 0
    }
    if (preferences.tab === PANEL_TAB.LIBRARY) {
      return bodyHeight > 0 ? bodyHeight : '100%'
    }
    return bodyHeight > 0 ? Math.min(libraryHeight, Math.max(bodyHeight - HANDLE_PX, 0)) : libraryHeight
  }

  const startResize = (axis, event) => {
    event.preventDefault()
    event.stopPropagation()
    const start = { x: event.clientX, y: event.clientY, width: preferences.width, libraryHeight }
    const move = (moveEvent) => onPreferencesChange(resizedPreferences(axis, start, { x: moveEvent.clientX, y: moveEvent.clientY }))
    // A resize follows the mouse at once: the zones only glide when the tab changes.
    setResizing(true)
    const stop = () => {
      setResizing(false)
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', stop)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', stop)
  }

  if (!open) {
    return (
      <Box data-testid='process-library-pill' sx={{ position: 'absolute', left: 0, top: 0 }}>
        {/* Floats like the open panel: an in-flow box would open a second row in the canvas grid and halve the graph. */}
        <EdgeButton
          testId='process-library-pill-button'
          open={false}
          disabled={!editing}
          label={t_('Process and library')}
          onClick={() => onPreferencesChange({ open: true })}
          style={{ left: 0, height: panelHeight }}
        />
      </Box>
    )
  }

  return (
    <>
      <Box
        data-node-toolbar-obstacle
        data-testid='process-library-panel'
        data-tab={preferences.tab}
        // The resize edge keeps a strip of its own, so the lists' scrollbars never sit under it (PO, 29/09/2026).
        style={{ width: preferences.width, height: panelHeight, paddingRight: HANDLE_PX }}
        sx={{ position: 'absolute', left: 0, top: 0, zIndex: 6, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', borderRight: 1, borderBottom: 1, borderColor: 'divider', boxShadow: 3, overflow: 'hidden' }}
      >
        <Stack direction='row' alignItems='center' sx={{ flex: '0 0 auto', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={preferences.tab} onChange={(event, tab) => onPreferencesChange({ tab })} variant='fullWidth' sx={{ flexGrow: 1, minHeight: 36 }}>
            {TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={t_(tab.labelKey)}
                data-testid={'process-library-tab-' + tab.value}
                // No MUI minimum (90 px): the three shrink with the panel down to its 220 px and grow with it (PO, 28/09/2026).
                sx={{ minHeight: 36, minWidth: 0, px: 0.5, textTransform: 'none' }}
              />
            ))}
          </Tabs>
        </Stack>
        <Box ref={bodyRef} sx={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Both zones stay mounted and laid out (CA1.4): a tab only moves the line between them. */}
          <Box
            data-testid='process-library-tree-zone'
            aria-hidden={preferences.tab === PANEL_TAB.LIBRARY}
            sx={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            {processTree}
          </Box>
          <Box
            data-testid='process-library-split'
            role='separator'
            aria-orientation='horizontal'
            aria-hidden={!mixed}
            onMouseDown={(event) => startResize(RESIZE_AXIS.SPLIT, event)}
            style={{ height: mixed ? HANDLE_PX : 0, transition: resizing ? 'none' : ZONE_TRANSITION }}
            // #215 (PO, 20/10/2026): the grip says it can be dragged; `overflow` hides it while the split is folded to 0.
            sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'row-resize', bgcolor: 'divider', '&:hover': { bgcolor: 'primary.main' }, '&:hover > *': { borderColor: 'primary.contrastText' } }}
          >
            <ResizeGrip />
          </Box>
          <Box
            data-testid='process-library-library-zone'
            aria-hidden={preferences.tab === PANEL_TAB.PROCESS}
            style={{ height: libraryTarget(), transition: resizing ? 'none' : ZONE_TRANSITION }}
            sx={{ flex: '0 0 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            {library}
          </Box>
        </Box>
        <Box
          data-testid='process-library-resize-width'
          onMouseDown={(event) => startResize(RESIZE_AXIS.WIDTH, event)}
          // #215 (PO, 20/10/2026): a tone of its own and the grip, so the edge reads as something to drag.
          sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: HANDLE_PX, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'col-resize', bgcolor: 'divider', transition: 'background-color 120ms', '&:hover': { bgcolor: 'primary.main' }, '&:hover > *': { borderColor: 'primary.contrastText' } }}
        >
          <ResizeGrip vertical />
        </Box>
      </Box>
      <EdgeButton
        testId='process-library-collapse'
        open
        label={t_('Process and library')}
        onClick={() => onPreferencesChange({ open: false })}
        style={{ left: preferences.width, height: panelHeight }}
      />
    </>
  )
}

export default ProcessLibraryPanel
